import { useCurrency } from '@/lib/currency';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowRightIcon,
  BriefcaseIcon,
  BuildingIcon,
  ClipboardCheckIcon,
  PhoneIcon,
  StarIcon,
  TargetIcon,
  UsersIcon,
} from '@/components/icons';
import { Tabs, TabPanel } from '@/components/ui/Tabs';
import { ROUTES } from '@/app/router/routes';
import { formatNumber, formatPercent } from '../dashboard-utils';
import type { DashboardActivity, DashboardData, DashboardMetric, DashboardRatio } from '../types';

const metricIcons = [
  UsersIcon,
  TargetIcon,
  BuildingIcon,
  BriefcaseIcon,
  StarIcon,
  ClipboardCheckIcon,
  PhoneIcon,
];
export const DashboardMetrics = ({ metrics }: { metrics: DashboardMetric[] }) => {
  useCurrency();
  return (
    <div className={`dash-metrics dash-metrics--${String(metrics.length)}`}>
      {metrics.map((metric, index) => {
        const Icon = metricIcons[index % metricIcons.length]!;
        return (
          <article
            key={metric.id}
            className={`dash-metric ${index === 0 ? 'dash-metric--primary' : ''}`}
          >
            <div className="dash-metric__top">
              <span>{metric.label}</span>
              <Icon />
            </div>
            <div className="dash-metric__value">
              <strong>{formatNumber(metric.value, metric.unit ?? 'number')}</strong>
              {metric.share !== null && <span>{formatPercent(metric.share)}</span>}
            </div>
            <p>
              {metric.share !== null ? 'du total · ' : ''}
              {metric.detail}
            </p>
            {metric.comparison && (
              <div className="dash-metric__change">
                <span>
                  {metric.change == null
                    ? '—'
                    : `${metric.change > 0 ? '+' : ''}${formatPercent(metric.change)}`}
                </span>
                <small>
                  {metric.change == null ? 'Sans base de comparaison' : metric.comparison}
                </small>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};

export const DashboardRatios = ({ ratios }: { ratios: DashboardRatio[] }) => {
  useCurrency();
  return (
    <section className="dash-card dash-ratios" aria-labelledby="dash-ratios-title">
      <header className="dash-card__head">
        <div>
          <span className="dash-eyebrow">Du vivier à la validation</span>
          <h3 id="dash-ratios-title">Les étapes de votre recrutement</h3>
          <p>Ratios sur des candidats distincts, dans le périmètre sélectionné.</p>
        </div>
        <span className="dash-soft-label">4 indicateurs</span>
      </header>
      <div className="dash-ratios__grid">
        {ratios.map((ratio, index) => (
          <article key={ratio.id}>
            <div className="dash-ratios__label">
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h4>{ratio.label}</h4>
            </div>
            <div className="dash-ratios__value">
              <strong>{formatPercent(ratio.value)}</strong>
              <span>
                {formatNumber(ratio.numerator)} <small>/ {formatNumber(ratio.denominator)}</small>
              </span>
            </div>
            <svg viewBox="0 0 300 8" preserveAspectRatio="none" aria-hidden="true">
              <rect width="300" height="8" rx="4" fill="#eeeaf6" />
              <rect width={(ratio.value ?? 0) * 3} height="8" rx="4" fill="#8862d2" />
            </svg>
            <p>
              {ratio.value === null
                ? 'Pas encore de base de comparaison'
                : 'candidats concernés / candidats éligibles'}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
};

const activityLink = (item: DashboardActivity) =>
  item.target === 'candidate'
    ? ROUTES.candidate(item.targetId)
    : item.target === 'requirement'
      ? ROUTES.requirement(item.targetId)
      : ROUTES.contact(item.targetId);
export const DashboardActivities = ({ groups }: { groups: DashboardData['activities'] }) => {
  useCurrency();
  const [tab, setTab] = useState(groups[0]?.id ?? 'candidates');
  const selected = groups.find((group) => group.id === tab);
  return (
    <section className="dash-card dash-activities" aria-labelledby="dash-activity-title">
      <header className="dash-card__head">
        <div>
          <span className="dash-eyebrow">Le fil de votre activité</span>
          <h3 id="dash-activity-title">Les dernières mises à jour</h3>
          <p>Retrouvez rapidement les fiches et échanges récents.</p>
        </div>
        <span className="dash-soft-label">8 derniers par catégorie</span>
      </header>
      <Tabs
        id="dash-activities"
        tabs={groups.map((group) => ({ value: group.id, label: group.label }))}
        value={tab}
        onChange={setTab}
        size="sm"
        label="Dernières activités"
      />
      <TabPanel id={`dash-activities-${tab}-panel`} labelledBy={`dash-activities-${tab}`}>
        {selected?.items.length ? (
          <ul className="dash-activity-list">
            {selected.items.map((item) => (
              <li key={item.id}>
                <Link to={activityLink(item)}>
                  <span className="dash-activity-avatar" aria-hidden="true">
                    {item.target === 'requirement' ? (
                      <TargetIcon />
                    ) : (
                      item.label
                        .split(' ')
                        .slice(0, 2)
                        .map((part) => part[0])
                        .join('')
                    )}
                  </span>
                  <div className="dash-activity-identity">
                    <strong>{item.label}</strong>
                    <span>{item.subtitle}</span>
                  </div>
                  <span className="dash-activity-status">{item.status}</span>
                  <time dateTime={item.updatedAt}>
                    {new Date(item.updatedAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                    })}
                    <small>
                      {new Date(item.updatedAt).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </small>
                  </time>
                  <ArrowRightIcon className="dash-activity-arrow" />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="dash-activity-empty">
            Aucune mise à jour dans cette catégorie sur la période sélectionnée.
          </div>
        )}
      </TabPanel>
    </section>
  );
};
